import type { AdminGoalResponse } from '@manasil/shared';
import { useState } from 'react';
import { Shell } from '../components/Shell.js';
import { Empty, Field, Loading, Modal, errorMessage, useToast } from '../components/ui.js';
import { useCreateGoal, useGoals, useUpdateGoal } from '../lib/queries.js';

function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11.2 2.3a1.8 1.8 0 0 1 2.5 2.5L5.5 13 2 14l1-3.5z" />
      <path d="M10.4 3.1 12.9 5.6" />
    </svg>
  );
}

/** Keys are shouty snake case, and permanent. Derived from the name only at creation. */
function toKey(name: string): string {
  return name
    .toUpperCase()
    .trim()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

/**
 * The client calls these "categories"; the code, the API and the database call them goals.
 * Only the wording differs — renaming the concept end to end would mean an API change, a
 * data migration and a matching mobile release, to change a label.
 */
export function GoalsPage() {
  const { data: goals, isLoading } = useGoals();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminGoalResponse | null>(null);

  return (
    <Shell
      title="Categories"
      subtitle="What a user picks in onboarding, and the only thing that matches a journey to a person. Each journey belongs to exactly one."
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          New goal
        </button>
      }
    >
      {isLoading ? (
        <div className="card">
          <Loading />
        </div>
      ) : !goals || goals.length === 0 ? (
        <div className="card">
          <Empty
            mark="◎"
            title="No categories yet"
            hint="Onboarding cannot run without at least one."
          />
        </div>
      ) : (
        <div className="goal-grid">
          {goals.map((goal, index) => (
            <article key={goal.key} className="goal-card" data-retired={!goal.active}>
              <div className="goal-top">
                <div className="goal-order">{String(index + 1).padStart(2, '0')}</div>
                <div className="row" style={{ gap: 8 }}>
                  <span className={`pill ${goal.active ? 'pill-ok' : 'pill-draft'}`}>
                    {goal.active ? 'Active' : 'Retired'}
                  </span>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setEditing(goal)}
                    aria-label={`Edit ${goal.name.en}`}
                    title="Edit"
                  >
                    <PencilIcon />
                  </button>
                </div>
              </div>

              <div>
                <div className="goal-name">{goal.name.en}</div>
                <div className="goal-key">{goal.key}</div>
              </div>

              <div className="goal-stats">
                <div className="goal-stat">
                  <b>{goal.journeyCount}</b>
                  <span>tagged journeys</span>
                </div>
                <div className="goal-stat">
                  <b>{goal.userCount}</b>
                  <span>chosen by</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <p className="hint" style={{ maxWidth: '70ch' }}>
        Order here is the order onboarding shows them in. A goal is never deleted — retiring one
        stops it being offered, while every user who already chose it and every journey tagged with
        it keeps working.
      </p>

      {creating ? <CreateGoalModal onClose={() => setCreating(false)} /> : null}
      {editing ? <EditGoalModal goal={editing} onClose={() => setEditing(null)} /> : null}
    </Shell>
  );
}

function CreateGoalModal({ onClose }: { onClose: () => void }) {
  const create = useCreateGoal();
  const { push } = useToast();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [ta, setTa] = useState('');
  const [hi, setHi] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        key,
        name: {
          en: name.trim(),
          ...(ta.trim() ? { ta: ta.trim() } : {}),
          ...(hi.trim() ? { hi: hi.trim() } : {}),
        },
      });
      push(`Added “${name.trim()}”`);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title="New category"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={create.isPending || name.trim().length === 0 || key.length === 0}
          >
            {create.isPending ? 'Adding…' : 'Add goal'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <Field label="Name (English)">
        <input
          className="input"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            if (!keyTouched) setKey(toKey(event.target.value));
          }}
          placeholder="Stress"
        />
      </Field>

      <Field
        label="Key"
        hint="Permanent. Everything that references this category — users, journeys, analytics — uses it, so it can never be changed afterwards."
      >
        <input
          className="input mono"
          value={key}
          onChange={(event) => {
            setKeyTouched(true);
            setKey(toKey(event.target.value));
          }}
          placeholder="STRESS"
        />
      </Field>

      <div className="grid-2">
        <Field label="Tamil" hint="Optional.">
          <input className="input" value={ta} onChange={(e) => setTa(e.target.value)} />
        </Field>
        <Field label="Hindi" hint="Optional.">
          <input className="input" value={hi} onChange={(e) => setHi(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function EditGoalModal({ goal, onClose }: { goal: AdminGoalResponse; onClose: () => void }) {
  const update = useUpdateGoal();
  const { push } = useToast();
  const [name, setName] = useState(goal.name.en);
  const [ta, setTa] = useState(goal.name.ta ?? '');
  const [hi, setHi] = useState(goal.name.hi ?? '');
  const [active, setActive] = useState(goal.active);
  const [error, setError] = useState<string | null>(null);

  const retiring = goal.active && !active;
  const inUse = goal.journeyCount > 0 || goal.userCount > 0;

  async function submit() {
    setError(null);
    try {
      await update.mutateAsync({
        key: goal.key,
        name: {
          en: name.trim(),
          ...(ta.trim() ? { ta: ta.trim() } : {}),
          ...(hi.trim() ? { hi: hi.trim() } : {}),
        },
        active,
      });
      push(`Saved “${name.trim()}”`);
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title={goal.name.en}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={update.isPending || name.trim().length === 0}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <Field label="Key" hint="Permanent — this is what every reference to the category uses.">
        <input className="input mono" value={goal.key} readOnly disabled />
      </Field>

      <Field label="Name (English)">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <div className="grid-2">
        <Field label="Tamil" hint="Optional.">
          <input className="input" value={ta} onChange={(e) => setTa(e.target.value)} />
        </Field>
        <Field label="Hindi" hint="Optional.">
          <input className="input" value={hi} onChange={(e) => setHi(e.target.value)} />
        </Field>
      </div>

      <label className="row" style={{ gap: 9, cursor: 'pointer' }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span>
          Offered in onboarding
          <span className="hint" style={{ display: 'block' }}>
            Turn this off to retire the goal. Nothing is deleted.
          </span>
        </span>
      </label>

      {retiring && inUse ? (
        <div className="issue">
          <span className="issue-mark" aria-hidden="true">
            ●
          </span>
          <span>
            {goal.journeyCount > 0 ? `${goal.journeyCount} journeys are tagged with this` : ''}
            {goal.journeyCount > 0 && goal.userCount > 0 ? ' and ' : ''}
            {goal.userCount > 0 ? `${goal.userCount} users have chosen it` : ''}. They keep working
            — it simply stops being offered to new users.
          </span>
        </div>
      ) : null}
    </Modal>
  );
}
